// „Ustaw nowe hasło" — krok drugi, z linku w mailu (artboardy 05c–05f).
//
// Link jest sprawdzany ZANIM formularz się narysuje (GET /auth/reset/<token>). Bez tego
// ktoś, komu wygasł trzydziestominutowy link, wpisuje nowe hasło dwa razy, zatwierdza
// i dopiero wtedy dowiaduje się, że link jest martwy — razem z hasłem, które właśnie
// wymyślił. Sonda kosztuje jedno żądanie i zamienia to w jedno zdanie na wejściu.
//
// W trakcie sprawdzania stoi SZKIELET o kształcie 1:1 z gotowym formularzem (05c):
// etykieta, pole, reguła, etykieta, pole, zdanie, przycisk. Ekran nie skacze, gdy
// odpowiedź przyjdzie, i nikt nie czyta zdania „sprawdzam link…", którego i tak nie ma
// jak użyć.
//
// Reguła hasła stoi Z GÓRY, nie po odrzuceniu formularza: backend wymaga 8 znaków
// z cyfrą i wielką literą, a formularz, który mówi to dopiero po błędzie, zawiódł.

import { useEffect, useId, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router'
import AuthShell from '../components/AuthShell'
import { Button, Field, PasswordMeter } from '../ds'
import { api, ApiError } from '../lib/api'
import { useT } from '../lib/i18n'

type Probe = 'checking' | 'valid' | 'dead'

/** Ta sama reguła, co w rejestracji i w backendzie: 8 znaków, wielka litera, cyfra. */
const STRONG = /^(?=.*[A-Z])(?=.*\d).{8,}$/

export default function ResetPassword() {
  const t = useT()
  const navigate = useNavigate()
  const { token = '' } = useParams()
  const [probe, setProbe] = useState<Probe>('checking')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [weak, setWeak] = useState(false)
  const ruleId = useId()

  useEffect(() => {
    let alive = true
    api<{ valid: boolean }>(`/auth/reset/${encodeURIComponent(token)}`)
      .then((r) => { if (alive) setProbe(r.valid ? 'valid' : 'dead') })
      // A failed probe is not proof the link is dead — but showing the form and
      // failing on submit is worse than asking for a fresh link.
      .catch(() => { if (alive) setProbe('dead') })
    return () => { alive = false }
  }, [token])

  const mismatch = confirm.length > 0 && password !== confirm

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (mismatch) return
    // Reguła jest ZNANA z góry, więc odmawia jej formularz, a nie wymiana z serwerem:
    // każda taka podróż kosztuje człowieka hasło, które właśnie wymyślił (R8, przegląd D).
    if (!STRONG.test(password)) { setWeak(true); return }
    setBusy(true)
    setError(null)
    try {
      await api(`/auth/reset/${encodeURIComponent(token)}`, {
        method: 'POST',
        body: JSON.stringify({ password }),
      })
      // Every session this account had is now dead server-side (the backend
      // rotates session_token), so sign-in is the only next step.
      navigate('/login?password_reset=1', { replace: true })
    } catch (err) {
      const code = err instanceof ApiError ? (err.body as { error?: string })?.error : undefined
      setError(
        code === 'weak_password' ? t('auth.reset.weak')
        : code === 'invalid_token' ? t('auth.reset.dead')
        : t('auth.forgot.networkError'),
      )
      if (code === 'invalid_token') setProbe('dead')
    } finally {
      setBusy(false)
    }
  }

  if (probe === 'checking') {
    return (
      <AuthShell>
        <h1>{t('auth.reset.heading')}</h1>
        <div className="mp-auth-skeleton" aria-busy="true" aria-label={t('common.loading')}>
          <span className="mp-sk mp-sk-label" />
          <span className="mp-sk mp-sk-input" />
          {/* Bez paska reguły: pod polem hasła gotowego formularza nie rysuje się nic,
              dopóki ktoś nie zacznie pisać (miernik wstaje od pierwszego znaku). */}
          <span className="mp-sk mp-sk-label" />
          <span className="mp-sk mp-sk-input" />
          <span className="mp-sk mp-sk-note" />
          <span className="mp-sk mp-sk-input" />
        </div>
      </AuthShell>
    )
  }

  if (probe === 'dead') {
    // JEDNA droga wyjścia (artboard 05d): świeży link. Powrót do logowania stoi pod nią
    // jako cichy link, w strefie kciuka na 390.
    return (
      <AuthShell>
        <h1>{t('auth.reset.deadHeading')}</h1>
        <p className="mp-auth-body">{t('auth.forgot.linkLife')}</p>
        <Button variant="primary" to="/forgot">{t('auth.reset.requestNew')}</Button>
        <p className="mp-auth-foot is-centered">
          <Link to="/login" className="mn-lnk">{t('auth.forgot.backToLogin')}</Link>
        </p>
      </AuthShell>
    )
  }

  return (
    <AuthShell>
      <h1>{t('auth.reset.heading')}</h1>

      {error && <p className="mp-err" role="alert">{error}</p>}

      <form onSubmit={onSubmit}>
        <Field
          id="pass"
          type="password"
          label={t('auth.reset.passwordLabel')}
          autoComplete="new-password"
          autoFocus
          value={password}
          onChange={(e) => { setPassword(e.target.value); setWeak(false) }}
          onBlur={() => setWeak(password.length > 0 && !STRONG.test(password))}
          /* Reguła jako BŁĄD POLA, dokładnie jak w rejestracji — jedyny tekst tego
             pola i tylko po odmowie, nigdy proaktywnie. */
          error={weak ? t('auth.reset.hint') : undefined}
          aria-describedby={ruleId}
          required
        />
        {/* R8: reguły hasła nie niesie zdanie na ekranie — miernik pokazuje ją oku
            (jest `aria-hidden`), a tekst `sr-only` niżej mówi ją czytnikowi ekranu. */}
        <PasswordMeter value={password} />
        <span className="sr-only" id={ruleId}>{t('auth.reset.hint')}</span>
        <Field
          id="pass2"
          type="password"
          label={t('auth.reset.confirmLabel')}
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          error={mismatch ? t('auth.reset.mismatch') : undefined}
          required
        />
        {/* Fakt bezpieczeństwa nad akcją zapisu, nie pod nią (decyzja 9). */}
        <p className="mp-auth-body">{t('auth.reset.signOutNote')}</p>
        <Button type="submit" variant="primary" busy={busy} disabled={mismatch || !password || !confirm}>
          {t('auth.reset.submit')}
        </Button>
      </form>
    </AuthShell>
  )
}
